import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import BarChartIcon from "@mui/icons-material/BarChart";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  PageHeader,
  Card,
  DataTable,
  SearchInput,
  ConfirmDialog,
  ActionButton,
} from "../shared";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import { getAdDeliveryLabel } from "./masterOrgAdUtils";

const PAGE_LIMIT = 10;

const MasterOrgAdvertisements = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const token = props?.props?.profile?.token;
  // Admin-controlled flag (loaded into profile by AppLayout): may this club partner advertise?
  const canAdvertise =
    props?.props?.profile?.can_advertise ??
    props?.props?.profile?.data?.can_advertise ??
    false;

  const [adList, setAdList] = useState([]);
  const [subOrgs, setSubOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalAds, setTotalAds] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    action: null,
  });

  const headers = () => ({ "Content-Type": "application/json", "x-access-token": token });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 600);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (token) {
      fetchSubOrgs();
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchAds();
  }, [token, page, debouncedSearch]);

  const fetchSubOrgs = async () => {
    try {
      const data = await ApiServices.GetApiCall(ApiEndpoint.MASTER_ORG_SUB_ORGS, headers());
      if (data?.status && Array.isArray(data.data)) {
        setSubOrgs(data.data);
      }
    } catch {
      // non-critical — club names will fall back to IDs
    }
  };

  const fetchAds = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ADVERTISEMENT_LIST,
        JSON.stringify({ pagination: true, page: page + 1, limit: PAGE_LIMIT, search: debouncedSearch }),
        headers()
      );
      if (res?.status) {
        setAdList(res.data || []);
        setTotalAds(res.count || 0);
      } else {
        setAdList([]);
        setTotalAds(0);
        toast.error(res?.message || "Failed to fetch advertisements");
      }
    } catch {
      toast.error("Failed to fetch advertisements");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (ad) => {
    setConfirmDialog({
      open: true,
      title: "Remove Advertisement?",
      message: `Are you sure you want to remove the ad for "${ad.url_link}"? This cannot be undone.`,
      action: async () => {
        try {
          const res = await ApiServices.PostApiCall(
            ApiEndpoint.MASTER_ORG_ADVERTISEMENT_DELETE,
            JSON.stringify({ id: ad.id }),
            headers()
          );
          if (res?.status) {
            toast.success("Advertisement removed");
            fetchAds();
          } else {
            toast.error(res?.message || "Failed to remove");
          }
        } catch {
          toast.error("Failed to remove advertisement");
        }
      },
    });
  };

  const clubNamesForAd = (ad) => {
    const orgIds = ad.target_org_ids || [];
    if (orgIds.length === 0) return null;
    return orgIds.map((id) => {
      const match = subOrgs.find((o) => String(o.id) === String(id));
      return match?.name || `Club #${id}`;
    });
  };

  const columns = [
    {
      field: "logo_url",
      label: "Image",
      width: 70,
      mobileInlineWithTitle: true,
      render: (value) =>
        value ? (
          <img
            src={value}
            alt="Ad"
            style={{ width: 48, height: 60, objectFit: "cover", borderRadius: 6 }}
          />
        ) : (
          <Box
            sx={{
              width: 48,
              height: 60,
              borderRadius: "6px",
              backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.6rem", color: isDark ? "#64748b" : "#94a3b8" }}>No img</Typography>
          </Box>
        ),
    },
    {
      field: "url_link",
      label: "Destination URL",
      render: (value) => (
        <Typography
          component="a"
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{
            fontSize: "0.875rem",
            color: isDark ? "#60a5fa" : "#1976d2",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
            ...(isMobile
              ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px", display: "block" }
              : { wordBreak: "break-all", maxWidth: 200 }),
          }}
        >
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "id",
      label: "Target Clubs",
      mobileSameLineGroup: "meta",
      render: (_, row) => {
        const names = clubNamesForAd(row);
        if (!names) return (
          <Chip
            label="All clubs"
            size="small"
            sx={{ fontSize: "0.7rem", backgroundColor: isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.1)", color: isDark ? "#6ee7b7" : "#059669", borderRadius: "6px" }}
          />
        );
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {names.map((name) => (
              <Chip
                key={name}
                label={name}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.08)",
                  color: isDark ? "#a5b4fc" : "#6366f1",
                  borderRadius: "6px",
                }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "delivery",
      label: "Delivery",
      width: 120,
      mobileSameLineGroup: "meta",
      render: (value) => {
        const label = getAdDeliveryLabel(value);
        const isMobileOnly = !value || value === "mobile_only";
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: isMobileOnly
                ? isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.08)"
                : isDark ? "rgba(148,163,184,0.15)" : "rgba(148,163,184,0.12)",
              color: isMobileOnly
                ? isDark ? "#a5b4fc" : "#6366f1"
                : isDark ? "#cbd5e1" : "#64748b",
              borderRadius: "6px",
            }}
          />
        );
      },
    },
  ];

  const renderActions = (row) => (
    <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
      <Tooltip title="Analytics">
        <IconButton
          size="small"
          onClick={() => navigate(`/master-org/advertisement-analytics/${row.id}`)}
          sx={{
            color: isDark ? "#34d399" : "#059669",
            "&:hover": { backgroundColor: isDark ? "rgba(52,211,153,0.1)" : "rgba(5,150,105,0.1)" },
          }}
        >
          <BarChartIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton
          size="small"
          onClick={() => navigate(`/master-org/edit-advertisement/${row.id}`)}
          sx={{
            color: isDark ? "#818cf8" : "#1976d2",
            "&:hover": { backgroundColor: isDark ? "rgba(99,102,241,0.1)" : "rgba(25,118,210,0.1)" },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Remove">
        <IconButton
          size="small"
          onClick={() => handleDelete(row)}
          sx={{
            color: isDark ? "#f87171" : "#dc2626",
            "&:hover": { backgroundColor: isDark ? "rgba(239,68,68,0.1)" : "rgba(220,38,38,0.1)" },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box>
      <PageHeader
        title="Advertisements"
        subtitle="Manage ads shown to members in the mobile app"
        breadcrumbs={[{ label: "Club Partner" }, { label: "Advertisements" }]}
        actionLabel={canAdvertise ? "Add Advertisement" : undefined}
        actionIcon={canAdvertise ? <AddIcon /> : undefined}
        onAction={canAdvertise ? () => navigate("/master-org/add-advertisement") : undefined}
      >
        <ActionButton
          variant="outline"
          startIcon={<BarChartIcon />}
          onClick={() => navigate("/master-org/advertisement-analytics")}
        >
          All Analytics
        </ActionButton>
        <SearchInput
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          onClear={() => { setSearchQuery(""); setPage(0); }}
          placeholder="Search ads..."
          width={240}
        />
      </PageHeader>

      <Card noPadding>
        <DataTable
          columns={columns}
          data={adList}
          loading={loading}
          actions={renderActions}
          onRowClick={(row) => navigate(`/master-org/advertisement-analytics/${row.id}`)}
          pagination
          page={page}
          rowsPerPage={PAGE_LIMIT}
          totalCount={totalAds}
          onPageChange={(e, newPage) => setPage(newPage)}
          emptyMessage="No advertisements found"
          mobileTitleColumn="url_link"
        />
      </Card>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="error"
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={async () => {
          if (confirmDialog.action) await confirmDialog.action();
          setConfirmDialog({ open: false, title: "", message: "", action: null });
        }}
        onCancel={() => setConfirmDialog({ open: false, title: "", message: "", action: null })}
      />
    </Box>
  );
};

export default MasterOrgAdvertisements;
